import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatTime } from '../game/state.ts';
import { LEVEL_INFO, type Level } from '../game/types.ts';
import type { ModeName, NotesStart, Settings, StyleName } from '../storage/storage.ts';
import { CloseIcon } from './Icons.tsx';

/** A native <dialog> shown as a modal while `open` is true. */
function Modal({ open, onClose, labelledBy, className, children }: {
  open: boolean;
  onClose?: () => void;
  labelledBy: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`sheet ${className ?? ''}`}
      aria-labelledby={labelledBy}
      onCancel={(e) => {
        e.preventDefault();
        onClose?.();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose?.();
      }}
    >
      {open ? children : null}
    </dialog>
  );
}

function Choice<T extends string>({ name, legend, value, options, onChange }: {
  name: string;
  legend: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="choice">
      <legend>{legend}</legend>
      <div className="segmented">
        {options.map((o) => (
          <label key={o.value} className={value === o.value ? 'is-on' : ''}>
            <input
              type="radio"
              id={`${name}-${o.value}`}
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function SettingsDialog({ open, settings, onChange, onClose }: {
  open: boolean;
  settings: Settings;
  onChange: (s: Settings) => void;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="settings-title">
      <header className="sheet-head">
        <h2 id="settings-title">Settings</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close settings">
          <CloseIcon />
        </button>
      </header>
      <Choice<StyleName>
        name="style"
        legend="Style"
        value={settings.style}
        options={[
          { value: 'minimal', label: 'Minimal' },
          { value: 'playful', label: 'Playful' },
        ]}
        onChange={(style) => onChange({ ...settings, style })}
      />
      <Choice<ModeName>
        name="mode"
        legend="Mode"
        value={settings.mode}
        options={[
          { value: 'system', label: 'System' },
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
        ]}
        onChange={(mode) => onChange({ ...settings, mode })}
      />
      <Choice<NotesStart>
        name="notes-start"
        legend="When starting notes"
        value={settings.notesStart}
        options={[
          { value: 'ask', label: 'Ask' },
          { value: 'fill', label: 'Always fill' },
          { value: 'blank', label: 'Start blank' },
        ]}
        onChange={(notesStart) => onChange({ ...settings, notesStart })}
      />
      <label className="switch" htmlFor="show-timer">
        <span>
          Show timer
          <small>Time is still recorded when hidden.</small>
        </span>
        <input
          id="show-timer"
          type="checkbox"
          role="switch"
          checked={settings.showTimer}
          onChange={(e) => onChange({ ...settings, showTimer: e.target.checked })}
        />
      </label>
    </Modal>
  );
}

/** First tap of Notes in a game: fill all possibilities, or start blank. */
export function NotesStartDialog({ open, onChoose, onCancel }: {
  open: boolean;
  onChoose: (fill: boolean, remember: boolean) => void;
  onCancel: () => void;
}) {
  const [remember, setRemember] = useState(false);
  return (
    <Modal open={open} onClose={onCancel} labelledBy="notes-start-title" className="confirm">
      <h2 id="notes-start-title">Fill in all possible digits?</h2>
      <p className="confirm-body">You can strike digits off yourself as you go.</p>
      <label className="check-row" htmlFor="notes-remember">
        <input id="notes-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
        Don&apos;t ask again (change it in Settings)
      </label>
      <div className="sheet-actions row">
        <button type="button" className="btn" onClick={() => onChoose(false, remember)}>
          Start blank
        </button>
        <button type="button" className="btn primary" onClick={() => onChoose(true, remember)} autoFocus>
          Fill all
        </button>
      </div>
    </Modal>
  );
}

/** An in-page yes/no question. The safe choice is focused first. */
export function ConfirmDialog({ open, title, body, confirmLabel, cancelLabel = 'Cancel', danger, onConfirm, onCancel }: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal open={open} onClose={onCancel} labelledBy="confirm-title" className="confirm">
      <h2 id="confirm-title">{title}</h2>
      <p className="confirm-body">{body}</p>
      <div className="sheet-actions row">
        <button type="button" className="btn" onClick={onCancel} autoFocus>
          {cancelLabel}
        </button>
        <button type="button" className={`btn ${danger ? 'danger-solid' : 'primary'}`} onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export function WinDialog({ open, level, elapsedMs, checksUsed, hintsUsed, autoNotesUsed, onNewGame, onHome }: {
  open: boolean;
  level: Level;
  elapsedMs: number;
  checksUsed: number;
  hintsUsed: number;
  autoNotesUsed: boolean;
  onNewGame: () => void;
  onHome: () => void;
}) {
  return (
    <Modal open={open} labelledBy="win-title" className="win">
      <h2 id="win-title">Solved</h2>
      <p className="win-level">{LEVEL_INFO[level].name} puzzle</p>
      <dl className="win-stats">
        <div>
          <dt>Time</dt>
          <dd>{formatTime(elapsedMs)}</dd>
        </div>
        <div>
          <dt>Checks</dt>
          <dd>{checksUsed}</dd>
        </div>
        <div>
          <dt>Hints</dt>
          <dd>{hintsUsed}</dd>
        </div>
      </dl>
      {autoNotesUsed ? <p className="win-note">Auto notes used</p> : null}
      <div className="sheet-actions">
        <button type="button" className="btn primary" onClick={onNewGame} autoFocus>
          New {LEVEL_INFO[level].name} game
        </button>
        <button type="button" className="btn" onClick={onHome}>
          Choose level
        </button>
      </div>
    </Modal>
  );
}
